import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faUsers, faPlus, faSearch, faUserPlus, faLink } from '@fortawesome/free-solid-svg-icons';
import Swal from 'sweetalert2';
import ProviderShell, { useProviderApi } from './layouts/ProviderShell';

interface PatientRelationship {
    relationship_id: number;
    patient_id: string;
    first_name: string;
    last_name: string;
    middle_name: string;
    email: string;
    phone: string;
    date_of_birth: string | null;
    gender: string;
    status: 'active' | 'inactive';
    notes: string;
    date_enrolled: string;
}

const ProviderPatients: React.FC = () => {
    const { api } = useProviderApi();
    const navigate = useNavigate();

    const [patients, setPatients] = useState<PatientRelationship[]>([]);
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState('');
    const [statusFilter, setStatusFilter] = useState('');

    const [showAdd, setShowAdd] = useState(false);
    const [addMode, setAddMode] = useState<'new' | 'existing'>('new');
    const [newForm, setNewForm] = useState({ first_name: '', last_name: '', middle_name: '', email: '', phone: '', date_of_birth: '', gender: '' });
    const [existingId, setExistingId] = useState('');
    const [submitting, setSubmitting] = useState(false);

    useEffect(() => { document.title = 'My Patients - Uzima Healthcare'; }, []);

    const loadPatients = async () => {
        setLoading(true);
        try {
            const params = new URLSearchParams({ search, status: statusFilter });
            const res = await api.get(`practitioners/patients/?${params}`);
            if (res.data?.status === 'success') setPatients(res.data.data || []);
        } catch {
            setPatients([]);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { loadPatients(); }, [search, statusFilter]); // eslint-disable-line react-hooks/exhaustive-deps

    const submitNewPatient = async () => {
        if (!newForm.first_name || !newForm.last_name || !newForm.email || !newForm.date_of_birth || !newForm.gender) {
            Swal.fire({ icon: 'warning', title: 'Missing Fields', text: 'First name, last name, email, date of birth and gender are required.' });
            return;
        }
        setSubmitting(true);
        try {
            const res = await api.post('practitioners/patients/', newForm);
            if (res.data?.status === 'success') {
                Swal.fire({ icon: 'success', title: 'Patient Added', timer: 1500, showConfirmButton: false });
                setShowAdd(false);
                setNewForm({ first_name: '', last_name: '', middle_name: '', email: '', phone: '', date_of_birth: '', gender: '' });
                loadPatients();
            }
        } catch (err: any) {
            Swal.fire({ icon: 'error', title: 'Error', text: err?.response?.data?.message || 'Failed to add patient.' });
        } finally {
            setSubmitting(false);
        }
    };

    const submitExistingPatient = async () => {
        if (!existingId.trim()) {
            Swal.fire({ icon: 'warning', title: 'Missing Patient ID', text: 'Enter the patient\'s ID code.' });
            return;
        }
        setSubmitting(true);
        try {
            const res = await api.post('practitioners/patients/', { existing_patient_id: existingId.trim() });
            if (res.data?.status === 'success') {
                Swal.fire({ icon: 'success', title: 'Patient Linked', timer: 1500, showConfirmButton: false });
                setShowAdd(false);
                setExistingId('');
                loadPatients();
            }
        } catch (err: any) {
            Swal.fire({ icon: 'error', title: 'Error', text: err?.response?.data?.message || 'Failed to link patient.' });
        } finally {
            setSubmitting(false);
        }
    };

    const toggleStatus = async (rel: PatientRelationship) => {
        const nextStatus = rel.status === 'active' ? 'inactive' : 'active';
        try {
            await api.patch(`practitioners/patients/${rel.relationship_id}/`, { status: nextStatus });
            loadPatients();
        } catch {
            Swal.fire({ icon: 'error', title: 'Error', text: 'Failed to update patient status.' });
        }
    };

    return (
        <ProviderShell>
            <div className="d-flex justify-content-between align-items-center mb-4">
                <div>
                    <h2 className="fw-bold mb-1"><FontAwesomeIcon icon={faUsers} className="me-2 text-primary" />My Patients</h2>
                    <p className="text-muted mb-0">Your patient roster</p>
                </div>
                <button className="btn btn-primary" onClick={() => setShowAdd(true)}>
                    <FontAwesomeIcon icon={faPlus} className="me-2" />Add Patient
                </button>
            </div>

            <div className="card mb-4">
                <div className="card-body">
                    <div className="row g-3">
                        <div className="col-md-6">
                            <div className="input-group">
                                <span className="input-group-text"><FontAwesomeIcon icon={faSearch} /></span>
                                <input type="text" className="form-control" placeholder="Search by name, email, or patient ID..."
                                       value={search} onChange={(e) => setSearch(e.target.value)} />
                            </div>
                        </div>
                        <div className="col-md-3">
                            <select className="form-select" value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
                                <option value="">All Statuses</option>
                                <option value="active">Active</option>
                                <option value="inactive">Inactive</option>
                            </select>
                        </div>
                    </div>
                </div>
            </div>

            <div className="card">
                <div className="card-body p-0">
                    {loading ? (
                        <div className="text-center py-5">
                            <div className="spinner-border text-primary" role="status"><span className="visually-hidden">Loading...</span></div>
                        </div>
                    ) : patients.length === 0 ? (
                        <div className="text-center py-5">
                            <FontAwesomeIcon icon={faUsers} className="text-muted mb-3" style={{ fontSize: '2.5rem' }} />
                            <h5>No patients yet</h5>
                            <p className="text-muted">Add a new patient or link an existing one to start building your roster.</p>
                            <button className="btn btn-primary" onClick={() => setShowAdd(true)}>
                                <FontAwesomeIcon icon={faPlus} className="me-2" />Add Your First Patient
                            </button>
                        </div>
                    ) : (
                        <div className="table-responsive">
                            <table className="table table-hover align-middle mb-0">
                                <thead className="table-light">
                                    <tr><th>Patient</th><th>Patient ID</th><th>Contact</th><th>Enrolled</th><th>Status</th><th>Actions</th></tr>
                                </thead>
                                <tbody>
                                    {patients.map(p => (
                                        <tr key={p.relationship_id}>
                                            <td><strong>{p.first_name} {p.middle_name} {p.last_name}</strong></td>
                                            <td><span className="badge bg-light text-dark">{p.patient_id}</span></td>
                                            <td>
                                                <div className="small">{p.email}</div>
                                                {p.phone && <div className="small text-muted">{p.phone}</div>}
                                            </td>
                                            <td>{new Date(p.date_enrolled).toLocaleDateString()}</td>
                                            <td><span className={`badge ${p.status === 'active' ? 'bg-success' : 'bg-secondary'}`}>{p.status}</span></td>
                                            <td>
                                                <div className="d-flex gap-1">
                                                    <button className="btn btn-sm btn-outline-primary" onClick={() => navigate(`/institution/patient-history/${p.patient_id}`)}>
                                                        <FontAwesomeIcon icon={faUserPlus} /> History
                                                    </button>
                                                    <button className={`btn btn-sm ${p.status === 'active' ? 'btn-outline-secondary' : 'btn-outline-success'}`} onClick={() => toggleStatus(p)}>
                                                        {p.status === 'active' ? 'Deactivate' : 'Reactivate'}
                                                    </button>
                                                </div>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>
            </div>

            {showAdd && (
                <div className="modal d-block" style={{ background: 'rgba(0,0,0,0.5)' }} tabIndex={-1}>
                    <div className="modal-dialog modal-dialog-centered">
                        <div className="modal-content">
                            <div className="modal-header">
                                <h5 className="modal-title">Add Patient</h5>
                                <button type="button" className="btn-close" onClick={() => setShowAdd(false)}></button>
                            </div>
                            <div className="modal-body">
                                <ul className="nav nav-pills mb-3">
                                    <li className="nav-item">
                                        <button className={`nav-link ${addMode === 'new' ? 'active' : ''}`} onClick={() => setAddMode('new')}>
                                            <FontAwesomeIcon icon={faUserPlus} className="me-1" />New Patient
                                        </button>
                                    </li>
                                    <li className="nav-item">
                                        <button className={`nav-link ${addMode === 'existing' ? 'active' : ''}`} onClick={() => setAddMode('existing')}>
                                            <FontAwesomeIcon icon={faLink} className="me-1" />Link Existing
                                        </button>
                                    </li>
                                </ul>

                                {addMode === 'new' ? (
                                    <>
                                        <div className="row g-3 mb-3">
                                            <div className="col-md-4">
                                                <label className="form-label">First Name</label>
                                                <input type="text" className="form-control" value={newForm.first_name} onChange={(e) => setNewForm(f => ({ ...f, first_name: e.target.value }))} />
                                            </div>
                                            <div className="col-md-4">
                                                <label className="form-label">Middle Name</label>
                                                <input type="text" className="form-control" value={newForm.middle_name} onChange={(e) => setNewForm(f => ({ ...f, middle_name: e.target.value }))} />
                                            </div>
                                            <div className="col-md-4">
                                                <label className="form-label">Last Name</label>
                                                <input type="text" className="form-control" value={newForm.last_name} onChange={(e) => setNewForm(f => ({ ...f, last_name: e.target.value }))} />
                                            </div>
                                        </div>
                                        <div className="row g-3 mb-3">
                                            <div className="col-md-6">
                                                <label className="form-label">Email</label>
                                                <input type="email" className="form-control" value={newForm.email} onChange={(e) => setNewForm(f => ({ ...f, email: e.target.value }))} />
                                            </div>
                                            <div className="col-md-6">
                                                <label className="form-label">Phone</label>
                                                <input type="text" className="form-control" value={newForm.phone} onChange={(e) => setNewForm(f => ({ ...f, phone: e.target.value }))} />
                                            </div>
                                        </div>
                                        <div className="row g-3 mb-3">
                                            <div className="col-md-6">
                                                <label className="form-label">Date of Birth</label>
                                                <input type="date" className="form-control" value={newForm.date_of_birth} onChange={(e) => setNewForm(f => ({ ...f, date_of_birth: e.target.value }))} />
                                            </div>
                                            <div className="col-md-6">
                                                <label className="form-label">Gender</label>
                                                <select className="form-select" value={newForm.gender} onChange={(e) => setNewForm(f => ({ ...f, gender: e.target.value }))}>
                                                    <option value="">Select...</option>
                                                    <option value="male">Male</option>
                                                    <option value="female">Female</option>
                                                    <option value="other">Other</option>
                                                </select>
                                            </div>
                                        </div>
                                    </>
                                ) : (
                                    <div className="mb-3">
                                        <label className="form-label">Patient ID</label>
                                        <input type="text" className="form-control" placeholder="e.g. A4LI3XX" value={existingId} onChange={(e) => setExistingId(e.target.value)} />
                                        <div className="form-text">Enter the patient ID code of an existing Uzima patient to add them to your roster.</div>
                                    </div>
                                )}
                            </div>
                            <div className="modal-footer">
                                <button className="btn btn-light" onClick={() => setShowAdd(false)}>Cancel</button>
                                <button className="btn btn-primary" disabled={submitting} onClick={addMode === 'new' ? submitNewPatient : submitExistingPatient}>
                                    {submitting ? 'Saving...' : (addMode === 'new' ? 'Add Patient' : 'Link Patient')}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </ProviderShell>
    );
};

export default ProviderPatients;
